import React from "react";
import {
  Alert,
  Collapse,
  Space,
  Tag,
  Typography,
} from "antd";

const { Title, Text, Paragraph } = Typography;

const CodeBlock = ({ children }) => (
  <pre className="api-code-block">
    <code>{children}</code>
  </pre>
);

const MethodTag = ({ method }) => {
  const colors = {
    GET: "blue",
    POST: "green",
    PATCH: "orange",
    DELETE: "red",
  };

  return (
    <Tag color={colors[method] || "default"}>
      {method}
    </Tag>
  );
};

const EndpointHeader = ({
  method,
  path,
  description,
}) => (
  <div className="api-endpoint-header">
    <Space wrap>
      <MethodTag method={method} />

      <Text code>{path}</Text>

      <Text type="secondary">
        {description}
      </Text>
    </Space>
  </div>
);

const ApiEndpoints = () => {
  const items = [
    {
      key: "meta",
      label: (
        <EndpointHeader
          method="GET"
          path="/api/v1/meta"
          description="Fetch CRM reference data"
        />
      ),
      children: (
        <div className="api-endpoint-content">
          <Title level={5}>
            Fetch CRM Metadata
          </Title>

          <Paragraph>
            Returns branch-specific reference data required
            when creating or updating leads.
          </Paragraph>

          <Paragraph>
            Use this endpoint to obtain valid IDs for lead
            statuses, sources, purposes and custom fields.
          </Paragraph>

          <Text strong>Required Permission</Text>

          <div className="api-permission-line">
            <Tag>Authenticated API Key</Tag>
          </div>

          <Text strong>Request</Text>

          <CodeBlock>
{`GET /api/v1/meta

Authorization: Bearer ferp_live_your_api_key`}
          </CodeBlock>

          <Alert
            type="info"
            showIcon
            message="Reference IDs are branch-specific"
            description="Do not hard-code IDs from another CRM branch. Fetch metadata using the same API key that will be used to create or update leads."
          />
        </div>
      ),
    },

    {
      key: "create-lead",
      label: (
        <EndpointHeader
          method="POST"
          path="/api/v1/leads"
          description="Create a new CRM lead"
        />
      ),
      children: (
        <div className="api-endpoint-content">
          <Title level={5}>Create Lead</Title>

          <Paragraph>
            Creates a new lead synchronously and returns the
            generated lead ID.
          </Paragraph>

          <Text strong>Required Permission</Text>

          <div className="api-permission-line">
            <Tag color="green">leads.create</Tag>
          </div>

          <Text strong>Example Request</Text>

          <CodeBlock>
{`POST /api/v1/leads

{
  "name": "Website Lead",
  "phone": "+971500001234",
  "email": "lead@example.com",
  "company": "Example Company",
  "address": "Dubai, UAE",
  "country": "UAE",
  "profession": "Manager",

  "status_id": "STATUS_UUID",
  "source_id": "SOURCE_UUID",
  "purpose_id": "PURPOSE_UUID",
  "assigned_to": "USER_UUID",

  "custom_fields": [
    {
      "field_id": "CUSTOM_FIELD_UUID",
      "value": "Custom value"
    }
  ]
}`}
          </CodeBlock>

          <Text strong>Success Response</Text>

          <CodeBlock>
{`HTTP 201

{
  "success": true,
  "data": {
    "lead_id": "LEAD_UUID",
    "status_id": "STATUS_UUID"
  }
}`}
          </CodeBlock>

          <Alert
            style={{ marginTop: 16 }}
            type="warning"
            showIcon
            message="Status handling"
            description="If status_id is omitted, the API resolves the branch's configured default lead status. Invalid branch references are rejected."
          />
        </div>
      ),
    },

    {
      key: "get-lead",
      label: (
        <EndpointHeader
          method="GET"
          path="/api/v1/leads/:leadId"
          description="Fetch a lead by UUID"
        />
      ),
      children: (
        <div className="api-endpoint-content">
          <Title level={5}>Get Lead</Title>

          <Paragraph>
            Retrieves a single lead belonging to the branch
            associated with the API key.
          </Paragraph>

          <Text strong>Required Permission</Text>

          <div className="api-permission-line">
            <Tag color="blue">leads.read</Tag>
          </div>

          <Text strong>Example Request</Text>

          <CodeBlock>
{`GET /api/v1/leads/LEAD_UUID

Authorization: Bearer ferp_live_your_api_key`}
          </CodeBlock>

          <Paragraph type="secondary">
            A lead from another branch cannot be accessed using
            the current API key.
          </Paragraph>
        </div>
      ),
    },

    {
      key: "update-lead",
      label: (
        <EndpointHeader
          method="PATCH"
          path="/api/v1/leads/:leadId"
          description="Partially update a lead"
        />
      ),
      children: (
        <div className="api-endpoint-content">
          <Title level={5}>Update Lead by ID</Title>

          <Paragraph>
            Partially updates a lead. Only fields included in
            the request are changed.
          </Paragraph>

          <Text strong>Required Permission</Text>

          <div className="api-permission-line">
            <Tag color="orange">leads.update</Tag>
          </div>

          <Text strong>Example Request</Text>

          <CodeBlock>
{`PATCH /api/v1/leads/LEAD_UUID

{
  "name": "Updated Lead Name",
  "status_id": "STATUS_UUID",
  "source_id": "SOURCE_UUID",
  "purpose_id": "PURPOSE_UUID",
  "assigned_to": "USER_UUID"
}`}
          </CodeBlock>

          <Text strong>Status-only Example</Text>

          <CodeBlock>
{`{
  "status_id": "STATUS_UUID"
}`}
          </CodeBlock>

          <Text strong>Success Response</Text>

          <CodeBlock>
{`{
  "success": true,
  "data": {
    "lead_id": "LEAD_UUID"
  }
}`}
          </CodeBlock>
        </div>
      ),
    },

    {
      key: "update-identifier",
      label: (
        <EndpointHeader
          method="PATCH"
          path="/api/v1/leads/by-identifier"
          description="Update using phone or email"
        />
      ),
      children: (
        <div className="api-endpoint-content">
          <Title level={5}>
            Update Lead by Identifier
          </Title>

          <Paragraph>
            Finds a lead using its phone number or email address
            and applies a partial update.
          </Paragraph>

          <Text strong>Required Permission</Text>

          <div className="api-permission-line">
            <Tag color="orange">leads.update</Tag>
          </div>

          <Alert
            type="info"
            showIcon
            message="Important request structure"
            description='Update fields must be placed inside the "updates" object.'
            style={{ marginBottom: 16 }}
          />

          <Text strong>Using Phone</Text>

          <CodeBlock>
{`PATCH /api/v1/leads/by-identifier

{
  "phone": "+971500001234",
  "updates": {
    "status_id": "STATUS_UUID",
    "assigned_to": "USER_UUID"
  }
}`}
          </CodeBlock>

          <Text strong>Using Email</Text>

          <CodeBlock>
{`{
  "email": "lead@example.com",
  "updates": {
    "source_id": "SOURCE_UUID",
    "purpose_id": "PURPOSE_UUID"
  }
}`}
          </CodeBlock>

          <Text strong>Success Response</Text>

          <CodeBlock>
{`{
  "success": true,
  "data": {
    "lead_id": "LEAD_UUID"
  }
}`}
          </CodeBlock>
        </div>
      ),
    },

    {
      key: "notes",
      label: (
        <EndpointHeader
          method="POST"
          path="/api/v1/leads/:leadId/notes"
          description="Add a note to a lead"
        />
      ),
      children: (
        <div className="api-endpoint-content">
          <Title level={5}>Create Lead Note</Title>

          <Paragraph>
            Adds a note to an existing CRM lead.
          </Paragraph>

          <Text strong>Required Permission</Text>

          <div className="api-permission-line">
            <Tag color="green">leads.notes</Tag>
          </div>

          <Text strong>Example Request</Text>

          <CodeBlock>
{`POST /api/v1/leads/LEAD_UUID/notes

{
  "note": "Customer requested a callback tomorrow."
}`}
          </CodeBlock>
        </div>
      ),
    },
  ];

  return (
    <div className="api-doc-section">
      <Title level={4}>API Endpoints</Title>

      <Paragraph type="secondary">
        Click an endpoint to view its permissions,
        request structure and examples.
      </Paragraph>

      <Collapse
        items={items}
        bordered
        className="api-endpoints-collapse"
      />
    </div>
  );
};

export default ApiEndpoints;